import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Check,
  ArrowLeft,
  User,
  MapPin,
  Mail,
  Phone,
  Car,
  Shield,
  FileText,
  Edit,
  Loader,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import analyticsService from "../lib/AnalyticsService";

const ProfileSummaryScreen = ({ userData, onContinue, onEdit, onBack }) => {
  const { getIdToken } = useAuth();
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    analyticsService.trackPageView("profile_summary", {});
  }, []);

  // Fetch summary data from API
  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get Firebase token
        const token = await getIdToken();
        if (!token) {
          throw new Error("Authentication required");
        }

        // Fetch summary from API
        const response = await fetch(
          `${API_CONFIG.BASE_URL}/api/insurance-profile/insurance-profile/summary`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch summary: ${response.status}`);
        }

        const data = await response.json();
        const parsedSummary = {
          insurancePreferences: {},
          personalInfo: {},
          vehicleInfo_add_dimo_todo: { vehicles: [] },
          additionalInfo: {},
          documents: [],
        };

        // Parse interest survey responses
        if (data.interestSurveyResponse?.responses) {
          data.interestSurveyResponse.responses.forEach((response) => {
            const answer =
              response.selections?.[0]?.optionText ||
              response.selections?.[0]?.optionValue;

            switch (response.questionId) {
              case "SQUE_Wv7dh88Cd9": // Do you currently have auto insurance?
                parsedSummary.insurancePreferences.currentInsurance = answer;
                break;
              case "SQUE_ZoWFs5rGOV": // Are you shopping for new insurance?
                parsedSummary.insurancePreferences.shoppingForNew = answer;
                break;
              case "SQUE_pP46Pfkndr": // When are you looking to switch?
                parsedSummary.insurancePreferences.switchTimeline = answer;
                break;
              default:
                parsedSummary.insurancePreferences.shoppingForNew = answer;
                break;
            }
          });
        }

        // Parse profile survey responses
        if (data.profileSurveyResponse?.responses) {
          data.profileSurveyResponse.responses.forEach((response) => {
            const answer =
              response.selections?.[0]?.optionText ||
              response.selections?.[0]?.optionValue;

            switch (response.questionId) {
              case "SQUE_M6Yemn9f2s": // Are you a homeowner?
                parsedSummary.additionalInfo.homeowner = answer === "Yes";
                break;
              case "SQUE_mAEfOvCc8G": // Have you had a DUI or need an SR-22 form?
                parsedSummary.additionalInfo.duiOrSr22 = answer === "Yes";
                break;
              case "SQUE_bHb4gFrACM": // Who is your current insurer?
                parsedSummary.additionalInfo.currentInsurer = answer;
                break;
              case "SQUE_wIvGbUHtdb": // Have you had insurance in the last 30 days?
                parsedSummary.additionalInfo.hasRecentInsurance =
                  answer === "Yes";
                break;
              case "SQUE_S99VhmpIMS": //How old is your car
                parsedSummary.additionalInfo.carAge = answer;
                break;
              case "SQUE_aylTEZrHqY": //What type of vehicle do you primarily drive?
                parsedSummary.additionalInfo.typeOfVehicle = answer;
                break;
              case "SQUE_4vJMNFbLFy": //Do you want to sign up for Driver Performance Monitoring?
                parsedSummary.additionalInfo.driverMonitoring =
                  answer === "Yes";
                break;
              case "SQUE_C764CxHIN0": //Which bodily injury limits are closest to your current coverage?
                parsedSummary.additionalInfo.injuryLimits = answer;
                break;
              default:
                break;
            }
          });
        }

        // Parse user information
        if (data.user) {
          parsedSummary.personalInfo.name = `${data.user.firstName || ""} ${
            data.user.lastName || ""
          }`.trim();
          parsedSummary.personalInfo.email = data.user.email;
          parsedSummary.personalInfo.phone = data.user.phone;

          if (data.user.address) {
            const address = data.user.address;
            const addressParts = [
              address.addressLine1,
              address.addressLine2,
              address.city,
              address.state,
              address.zip,
            ].filter(Boolean);
            parsedSummary.personalInfo.address = addressParts.join(", ");
          }
        }

        // Parse policy/document information
        if (data.policy?.files) {
          parsedSummary.documents = data.policy.files.map((file) => ({
            name:
              file.url.split("/").pop().split("_").pop() ||
              "Insurance Document",
            status: data.policy.checkInStatus || "Completed",
            type: file.documentType,
            url: file.url,
          }));
        }

        setSummaryData(parsedSummary);
      } catch (err) {
        console.error("Error fetching summary:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummaryData();
  }, [getIdToken]);

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Back button for loading state */}
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        )}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Preparing Your Profile Summary
          </h1>
          <p className="text-gray-600">
            We're gathering all your information for review
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 flex justify-center">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600">Loading your profile...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
        )}
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Unable to Load Summary
          </h1>
          <p className="text-gray-600">
            We encountered an issue loading your profile summary
          </p>
        </div>

        <Card className="border-red-100 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">
                  Error Loading Profile
                </p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <button
            onClick={() => window.location.reload()}
            className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={onContinue}
            className="flex-1 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Continue Anyway
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}
      {/* Header */}
      <div className="text-center space-y-4">
        {/* <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-green-500" />
        </div> */}
        <h1 className="text-xl font-bold uppercase">
          Almost Done! Let's Double Check Everything
        </h1>
        <p className="text-darkGrey">
          Here's what you've shared so far. Please review and submit your
          profile so we can start finding buyers and offers.
        </p>
      </div>

      {/* Insurance Preferences Section */}
      {summaryData?.insurancePreferences && (
        <div className="border-b border-gray-300 mb-6">
          <p className="text-base font-bold mb-4">Insurance Preferences</p>
          {summaryData.insurancePreferences.currentInsurance && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-darkGrey">
                Do you currently have auto insurance?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.insurancePreferences.currentInsurance}
              </span>
            </div>
          )}
          {summaryData.insurancePreferences.shoppingForNew && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-darkGrey">
                Are you shopping for new insurance?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.insurancePreferences.shoppingForNew}
              </span>
            </div>
          )}
          {summaryData.insurancePreferences.switchTimeline && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                When are you planning to switch or buy?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.insurancePreferences.switchTimeline}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Personal Information Section */}
      {summaryData?.personalInfo && (
        <div className="border-b border-gray-300 mb-6">
          <p className="text-base font-bold mb-4">Personal Information</p>
          {summaryData.personalInfo.name && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs">{summaryData.personalInfo.name}</span>
            </div>
          )}
          {summaryData.personalInfo.address && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs">
                {summaryData.personalInfo.address}
              </span>
            </div>
          )}
          {summaryData.personalInfo.email && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs">{summaryData.personalInfo.email}</span>
            </div>
          )}
          {summaryData.personalInfo.phone && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs">{summaryData.personalInfo.phone}</span>
            </div>
          )}
        </div>
      )}

      {/* Vehicle Information Section */}
      {summaryData?.vehicleInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-500" />
              Vehicle Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {summaryData.vehicleInfo.vehicles &&
              summaryData.vehicleInfo.vehicles.map((vehicle, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Car className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </span>
                  {vehicle.vin && (
                    <span className="text-sm text-gray-500">
                      (VIN:{" "}
                      {vehicle.vin.slice(-4).padStart(vehicle.vin.length, "*")})
                    </span>
                  )}
                </div>
              ))}
            {(!summaryData.vehicleInfo.vehicles ||
              summaryData.vehicleInfo.vehicles.length === 0) && (
              <p className="text-gray-500 text-sm">
                No vehicle information provided
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Additional Information Section */}
      {summaryData?.additionalInfo && (
        <div className="border-b border-gray-300 mb-6">
          <p className="text-base font-bold mb-4">Additional Information</p>
          {summaryData.additionalInfo.homeowner !== undefined && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-darkGrey">
                Are you a homeowner?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.homeowner ? "Yes" : "No"}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.duiOrSr22 !== undefined && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-darkGrey">
                Have you had a DUI or need SR-22?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.duiOrSr22 ? "Yes" : "No"}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.hasRecentInsurance !== undefined && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-darkGrey">
                Have you had insurance in the last 30 days?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.hasRecentInsurance ? "Yes" : "No"}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.currentInsurer && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                Current Insurance Company
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.currentInsurer}
              </span>
            </div>
          )}

          {summaryData.additionalInfo.carAge && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                How old is your car?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.carAge}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.typeOfVehicle && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                What type of vehicle do you primarily drive?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.typeOfVehicle}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.driverMonitoring !== undefined && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                Do you want to sign up for Driver Performance Monitoring?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.driverMonitoring ? "Yes" : "No"}
              </span>
            </div>
          )}
          {summaryData.additionalInfo.injuryLimits && (
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-darkGrey">
                Which bodily injury limits are closest to your current coverage?
              </span>
              <span className="text-xs whitespace-nowrap">
                {summaryData.additionalInfo.injuryLimits}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Document Section */}
      {summaryData?.documents && summaryData.documents.length > 0 && (
        <div className="border-b border-gray-300 mb-6">
          <p className="text-base font-bold mb-4">Document</p>
          {summaryData.documents.map((doc, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4"
            >
              <FileText className="w-5 h-5 text-blue-500" />
              <div className="flex-1">
                <p className="font-medium">
                  {doc.name || "Insurance Document"}
                </p>
                <p className="text-sm text-gray-500">
                  {doc.size
                    ? `${(doc.size / (1024 * 1024)).toFixed(1)} MB`
                    : ""}{" "}
                  • {doc.status || "Completed"}
                </p>
              </div>
              <Check className="w-5 h-5 text-green-500" />
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        <button
          onClick={onContinue}
          className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient !mt-10 text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
        >
          <span>Submit</span>
        </button>

        {/* Edit button - optional, for future functionality */}
        {/* <button
          onClick={onEdit}
          className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Edit className="w-4 h-4" />
          <span>Edit Information</span>
        </button> */}
      </div>

      {/* Privacy Note */}
      {/* <p className="text-xs text-center text-gray-500">
        By submitting your profile, you're allowing Ownli to find relevant
        offers from insurance partners. Your data is protected with bank-level
        encryption and you can revoke access at any time.
      </p> */}
    </div>
  );
};

export default ProfileSummaryScreen;
